// Cart.tsx
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import Button from "components/common/Button";
import { useAppDispatch, useAppSelector } from "hooks/store";
// === ADDED BY SAMPATH – 2026-01-12 ===
// FIX: iPhone (Safari) layout issues -> dynamic footer height + iOS smooth scrolling
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
// === END SAMPATH ===
import {
  removeAllFromCart,
  removeFromCart,
  setCheckoutData,
  setSelected,
  // === ADDED BY SAMPATH – 2026-01-12 ===
  toggleCart,
  // === END SAMPATH ===
  // === ADDED BY SAMPATH – 2026-01-15 ===
  // Delivery type switch (Pickup/Delivery) from cart
  setdeliveryType,
  // === END SAMPATH ===
} from "store/slices/main.slice";
import { ORDER } from "utils/constants";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";

// === ADDED BY SAMPATH – 2026-01-12 ===
import CloseIcon from "@mui/icons-material/Close";
// === END SAMPATH ===

type Props = {
  setStage: (v: string) => void;
};

// === ADDED BY SAMPATH – 2026-01-05 ===
const getCartItemImage = (item: any): string => {
  return (
    item?.imageUrl ||
    item?.image ||
    item?.productImage ||
    item?.product?.imageUrl ||
    item?.product?.image ||
    item?.pizza?.imageUrl ||
    item?.pizza?.image ||
    item?.data?.imageUrl ||
    item?.data?.image ||
    ""
  );
};
// === END SAMPATH ===

// === ADDED BY SAMPATH – 2026-01-05 ===
const sanitizeForStructuredClone = <T,>(input: T): T => {
  const seen = new WeakSet<object>();

  const isDomNode = (v: any) =>
    typeof v === "object" &&
    v !== null &&
    (typeof (v as any).nodeType === "number" ||
      typeof (v as any).ownerDocument === "object");

  const isReactElementLike = (v: any) =>
    typeof v === "object" &&
    v !== null &&
    ("$$typeof" in v || "_owner" in v || "props" in v);

  const walk = (v: any): any => {
    if (v === null) return null;
    const t = typeof v;
    if (t === "string" || t === "number" || t === "boolean") return v;

    if (t === "function" || t === "symbol") return undefined;
    if (t === "bigint") return v.toString();
    if (v instanceof Date) return v.toISOString();

    if (isDomNode(v) || isReactElementLike(v)) return undefined;

    if (v instanceof Map)
      return Array.from(v.entries()).map(([k, val]) => [walk(k), walk(val)]);
    if (v instanceof Set) return Array.from(v.values()).map((val) => walk(val));

    if (Array.isArray(v))
      return v.map((x) => walk(x)).filter((x) => x !== undefined);

    if (typeof v === "object") {
      if (seen.has(v)) return undefined;
      seen.add(v);

      const out: any = {};
      Object.keys(v).forEach((key) => {
        const next = walk(v[key]);
        if (next !== undefined) out[key] = next;
      });
      return out;
    }

    return undefined;
  };

  return walk(input) as T;
};
// === END SAMPATH ===

// === ADDED BY SAMPATH – 2026-01-05 ===
const CartImage = ({ src, alt }: { src?: string; alt: string }) => {
  const [ok, setOk] = useState(true);

  if (!src || !ok) {
    return (
      <div className="h-[56px] w-[56px] sm:h-[64px] sm:w-[64px] rounded-xl bg-gray-100 border border-gray-200 flex items-center justify-center text-[10px] text-gray-500">
        No Image
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className="h-[56px] w-[56px] sm:h-[64px] sm:w-[64px] rounded-xl object-cover border border-gray-200 bg-white"
      onError={() => setOk(false)}
      loading="lazy"
    />
  );
};
// === END SAMPATH ===

// === ADDED BY SAMPATH – 2026-01-05 ===
const Pill = ({
  children,
  className = "",
}: {
  children: any;
  className?: string;
}) => (
  <div
    className={
      "px-3 py-1.5 rounded-full bg-white border border-gray-200 text-[12px] font-bold text-gray-600 shadow-sm " +
      className
    }
  >
    {children}
  </div>
);

const DividerDot = () => (
  <div className="flex-1 border-b border-dashed border-gray-300 mx-3 translate-y-[-2px]" />
);
// === END SAMPATH ===

const Item = ({ item, index, onAction, showDefaultToppings = 1 }: any) => {
  const toppings = useMemo(
    () =>
      item?.modifiers?.filter(
        (i: any) =>
          showDefaultToppings || !i.defaultTopping || i.newDefaultTopping
      ),
    [showDefaultToppings, item?.modifiers]
  );

  const isDeal =
    item.data &&
    item.data.selectedDealPizzas &&
    item.data.selectedDealPizzas.length > 0;

  const productImg = getCartItemImage(item);

  return (
    <div className="mx-4 my-2 rounded-2xl border border-gray-200 bg-white shadow-sm hover:shadow-md transition-shadow">
      <div className="p-3 sm:p-4 flex flex-col sm:flex-row gap-3 sm:gap-4">
        <div className="flex items-start gap-3 sm:gap-4">
          <div className="relative">
            <CartImage src={productImg} alt={item?.fullDescription || "Item"} />
            <div className="absolute -top-2 -left-2 h-6 min-w-[24px] px-1 rounded-full bg-primary text-light-text text-[11px] font-bold flex items-center justify-center border border-white shadow">
              {item?.quantity ?? 1}
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <p className="font-bold text-[14px] sm:text-[15px] text-gray-600 truncate">
              {item.fullDescription}
            </p>

            {isDeal ? (
              <>
                <p className="mt-1 text-[12px] text-gray-600 font-semibold">
                  Deal Items:
                </p>
                {item.data.selectedDealPizzas.map(
                  (dealPizza: any, dealIndex: number) => (
                    <div key={dealIndex} className="mt-1 pl-2 border-l">
                      <p className="text-[12px] text-gray-700">
                        <b>Pizza {dealIndex + 1}:</b>{" "}
                        {dealPizza.pizzaName ?? dealPizza.fullDescription}
                      </p>
                      <p className="text-[12px] text-gray-700">
                        <b>Size:</b>{" "}
                        {`${dealPizza.pizzaSize.size} ${
                          item.pizzaSize.price ? `[$${item.pizzaSize.price}]` : ""
                        }`}
                      </p>
                      {!!dealPizza.spiceLevel?.spicename && (
                        <p className="text-[12px] text-gray-700">
                          <b>Spicy:</b> {dealPizza.spiceLevel?.spicename}
                        </p>
                      )}
                      {dealPizza.modifiers && dealPizza.modifiers.length > 0 && (
                        <p className="text-[12px] text-gray-700">
                          <b>Toppings:</b>{" "}
                          {dealPizza.modifiers
                            .filter(
                              (_: any, index: number) =>
                                index >= item.data.toppingsEachPizza
                            )
                            .map(
                              (mod: any) =>
                                `${mod.description} ${
                                  mod.subTotal ? `[$${mod.subTotal}]` : ""
                                }`
                            )
                            .join(", ")}
                        </p>
                      )}
                      <p className="text-[12px] text-gray-700">
                        <b>Note:</b> {dealPizza?.note || ""}
                      </p>
                    </div>
                  )
                )}
              </>
            ) : (
              <>
                {!!item.isPizza && (
                  <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[12px] text-gray-700">
                    <p>
                      <b>Size:</b>{" "}
                      {`${item.pizzaSize.size} ${
                        item.pizzaSize.price ? `[$${item.pizzaSize.price}]` : ""
                      }`}
                    </p>
                    {!!item?.spiceLevel?.spicename && (
                      <p>
                        <b>Spicy:</b> {item?.spiceLevel?.spicename}
                      </p>
                    )}
                    <p>
                      <b>Crust:</b>{" "}
                      {`${item.pizzaCrust?.crustName} ${
                        item.pizzaCrust?.crustPrice
                          ? `[$${item.pizzaCrust?.crustPrice}]`
                          : ""
                      }`}
                    </p>
                  </div>
                )}

                {!item.isPizza && item.selectedItemsLevel?.length > 0 && (
                  <div className="mt-1">
                    <p className="text-[12px] text-gray-700 break-words">
                      {item.selectedItemsLevel
                        .map(
                          (i: any) => i.name + (!!i.price ? " $" + i.price : "")
                        )
                        .join(", ")}
                    </p>
                  </div>
                )}

                {!!toppings?.length && (
                  <p className="mt-1 text-[12px] text-gray-700 break-words">
                    <b>Toppings:</b>{" "}
                    {toppings
                      .map((i: any) => {
                        let v = i.description;
                        if (!i.newDefaultTopping && i.subTotal) {
                          v += ` [$${i.subTotal}]`;
                        }
                        return v;
                      })
                      .join(", ")}
                  </p>
                )}

                {item.note && (
                  <p className="mt-1 text-[12px] text-gray-900 break-words">
                    <b>Note:</b> {item.note || ""}
                  </p>
                )}
              </>
            )}
          </div>
        </div>

        <div className="sm:ml-auto sm:text-right flex sm:flex-col justify-between sm:justify-start gap-2 sm:gap-1 pt-2 sm:pt-0 border-t sm:border-t-0">
          <div className="flex sm:flex-col items-start sm:items-end gap-2 sm:gap-0">
            <p className="font-bold text-[13px] sm:text-[14px] text-gray-600">
              ${item.subTotal.toFixed(2)}
            </p>
            <p className="text-[12px] text-gray-600">
              <b>Qty:</b> {item.quantity}
            </p>
          </div>

          <div className="flex sm:justify-end gap-2">
            <button
              type="button"
              className="text-[12px] font-semibold text-primary underline underline-offset-2"
              onClick={() => onAction("edit", { ...item, index, isEdit: true })}
            >
              Edit
            </button>
            <button
              type="button"
              className="text-[12px] font-semibold text-red-600 underline underline-offset-2"
              onClick={() => onAction("delete", { index, item })}
            >
              Remove
            </button>
          </div>

          <div className="hidden">
            <div
              className="bg-primary rounded-md h-[22px] w-[22px] flex justify-center items-center cursor-pointer"
              onClick={() => onAction("edit", { ...item, index, isEdit: true })}
            >
              <EditIcon className="!text-[15px] !text-light-text" />
            </div>
            <div
              className="bg-primary rounded-md h-[22px] w-[22px] flex justify-center items-center cursor-pointer"
              onClick={() => onAction("delete", { index, item })}
            >
              <DeleteIcon className="!text-[15px] !text-light-text" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const CartView = ({ setStage }: Props) => {
  const dispatch = useAppDispatch();

  const { cart, taxes, settings, checkoutData, deliveryType, companyData } =
    useAppSelector(({ main }) => main);

  const isCartOpen = useAppSelector(({ main }: any) => {
    const v =
      main?.isCartOpen ??
      main?.cartOpen ??
      main?.openCart ??
      main?.cartDrawerOpen ??
      main?.isCartDrawerOpen ??
      main?.showCart ??
      undefined;
    return typeof v === "boolean" ? v : true;
  });

  const [orderType, setOrderType] = useState("");
  const [deleteDialog, setDeleteDialog] = useState<any>(undefined);

  // === ADDED BY SAMPATH – 2026-01-15 ===
  // Total Qty in cart (if qty becomes 0, auto-close cart)
  const cartTotalQty = useMemo(() => {
    return (cart || []).reduce((sum: number, i: any) => sum + (Number(i?.quantity) || 0), 0);
  }, [cart]);
  // === END SAMPATH ===

  // === ADDED BY SAMPATH – 2026-01-15 ===
  // Auto close cart when cart is empty OR total qty is 0
  useEffect(() => {
    if (!isCartOpen) return;
    const isEmpty = !(cart && cart.length);
    const isQtyZero = cartTotalQty <= 0;
    if (isEmpty || isQtyZero) {
      setDeleteDialog(undefined);
      dispatch(toggleCart());
    }
  }, [isCartOpen, cart?.length, cartTotalQty, dispatch]);
  // === END SAMPATH ===

  // === ADDED BY SAMPATH – 2026-01-12 ===
  // FIX (iPhone): measure sticky footer height dynamically so item list padding is correct on ALL devices.
  const footerRef = useRef<HTMLDivElement | null>(null);
  const [footerHeight, setFooterHeight] = useState(0);

  useLayoutEffect(() => {
    if (!isCartOpen) return;
    const el = footerRef.current;
    if (!el) return;

    const update = () => setFooterHeight(el.getBoundingClientRect().height || 0);
    update();

    const ro = new ResizeObserver(() => update());
    ro.observe(el);

    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, [isCartOpen, cart?.length]);
  // === END SAMPATH ===

  // === ADDED BY SAMPATH – 2026-01-14 ===
  const REMOVE_DIALOG_ZINDEX = 10050;
  // === END SAMPATH ===

  // === ADDED BY SAMPATH – 2026-01-15 ===
  // Allow user to switch delivery type (Pickup/Delivery) directly from cart.
  // Respect store settings that may close pickup/delivery.
  const isPickupDisabled = !!settings?.ClosePickupOrder;
  const isDeliveryDisabled = !!settings?.CloseDeliveryOrder;

  const changeDeliveryType = (next: "pickup" | "delivery") => {
    if (next === "pickup" && isPickupDisabled) return;
    if (next === "delivery" && isDeliveryDisabled) return;
    dispatch(setdeliveryType(next));
  };
  // === END SAMPATH ===

  useEffect(() => {
    if (settings.ClosePickupOrder) {
      setOrderType(ORDER.DELIVERY);
      return;
    }
    if (settings.CloseDeliveryOrder) {
      setOrderType(ORDER.TAKE_OUT);
      return;
    }
    setOrderType(checkoutData?.orderType || ORDER.TAKE_OUT);
    if (settings.ApplyTakeOutDeliveryPrice !== 0) {
      if (deliveryType !== "pickup") {
        setOrderType(ORDER.DELIVERY);
      } else {
        setOrderType(ORDER.TAKE_OUT);
      }
    } else {
      if (deliveryType !== "pickup") {
        setOrderType(ORDER.DELIVERY);
      } else {
        setOrderType(ORDER.TAKE_OUT);
      }
    }
  }, [checkoutData, settings, deliveryType]);

  useEffect(() => {
    if (!isCartOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isCartOpen]);

  const placeOrder = () => {
    // === ADDED BY SAMPATH – 2026-01-15 ===
    // Close cart + overlay FIRST (so overlay is not visible behind DETAILS dialog)
    // Also close any open remove dialog.
    setDeleteDialog(undefined);
    dispatch(toggleCart());

    // Open DETAILS after cart state updates / animation frame (prevents overlay flash)
    const openDetails = () => setStage("DETAILS");
    if (typeof window !== "undefined" && "requestAnimationFrame" in window) {
      window.requestAnimationFrame(openDetails);
    } else {
      setTimeout(openDetails, 0);
    }
    // === END SAMPATH ===

    const safeCart = sanitizeForStructuredClone(cart);
    dispatch(setCheckoutData({ orderType, finalBill, total, cart: safeCart }));
  };

  const handleCartAction = (type: string, data: any) => {
    if (type === "edit") {
      // === ADDED BY SAMPATH – 2026-01-15 ===
      // When user clicks Edit -> close cart first, then open popup (setSelected)
      setDeleteDialog(undefined);
      if (isCartOpen) {
        dispatch(toggleCart());
        const safeSelected = sanitizeForStructuredClone(data);
        const openPopup = () => dispatch(setSelected(safeSelected));
        if (typeof window !== "undefined" && "requestAnimationFrame" in window) {
          window.requestAnimationFrame(openPopup);
        } else {
          setTimeout(openPopup, 0);
        }
      } else {
        const safeSelected = sanitizeForStructuredClone(data);
        dispatch(setSelected(safeSelected));
      }
      return;
      // === END SAMPATH ===
    }
    if (type === "delete") {
      setDeleteDialog(data);
    }
  };

  const deliveryCharge = useMemo(() => {
    if (orderType !== ORDER.DELIVERY) return 0;
    return companyData?.deliveryCharge || 0;
  }, [orderType, companyData?.deliveryCharge]);

  const { finalBill, total } = useMemo(() => {
    const list: any[] = [
      { title: "SubTotal", amt: 0 },
      ...taxes.map((i) => ({ title: i.name, amt: 0 })),
    ];
    let allTaxes = 0;

    const subTotal = cart.reduce((t, item) => {
      const itemSubTotal =
        item.data && item.data.selectedDealPizzas
          ? item.data.selectedDealPizzas.reduce(
              (dealTotal: number, dealPizza: any) =>
                dealTotal + dealPizza.subTotal,
              0
            )
          : item.subTotal;

      if (item.totalTax1 && item.taxId1 > 0) {
        list[1].amt += item.totalTax1;
        allTaxes += item.totalTax1;
      }
      if (item.totalTax2 && item.taxId2 > 0) {
        list[2].amt += item.totalTax2;
        allTaxes += item.totalTax2;
      }
      if (item.totalTax3 && item.taxId3 > 0) {
        list[3].amt += item.totalTax3;
        allTaxes += item.totalTax3;
      }
      return t + itemSubTotal;
    }, 0);
    list[0].amt = subTotal;

    return { finalBill: list, total: subTotal + allTaxes };
  }, [cart, taxes]);

  return (
    <div
      className={
        "fixed inset-0 z-[9999] " +
        (isCartOpen ? "pointer-events-auto" : "pointer-events-none")
      }
      aria-hidden={!isCartOpen}
    >
      {/* Overlay */}
      <div
        className={
          "absolute inset-0 transition-opacity duration-300 " +
          (isCartOpen ? "opacity-100" : "opacity-0")
        }
        style={{
          backgroundColor: "rgb(var(--color-primary) / 0.18)",
          backdropFilter: "blur(2px)",
        }}
        onClick={() => dispatch(toggleCart())}
      />

      {/* Drawer Panel */}
      <div
        className={
          "absolute right-0 top-0 h-full w-full sm:w-[420px] sm:max-w-[420px] bg-white border-l border-gray-200 shadow-2xl " +
          "transition-transform duration-300 ease-out " +
          (isCartOpen ? "translate-x-0" : "translate-x-full")
        }
        style={{
          backgroundColor: "rgb(255 255 255 / 0.94)",
          backdropFilter: "blur(8px)",
          height: "100dvh",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-full flex flex-col">
          {/* Sticky top mini header */}
          <div className="shrink-0 border-b border-gray-200">
            <div
              className="px-4 py-3 flex items-center justify-between"
              style={{
                backgroundColor: "rgb(var(--color-primary) / 0.06)",
                paddingTop: "calc(0.75rem + env(safe-area-inset-top))",
              }}
            >
              <div className="flex items-center gap-2 min-w-0">
                <h4 className="!text-[18px] !text-primary !font-[600] tracking-tight truncate">
                  My Cart
                </h4>
                <span
                  className="text-[12px] font-bold px-2 py-0.5 rounded-full border"
                  style={{
                    color: "rgb(var(--color-primary) / 1)",
                    borderColor: "rgb(var(--color-primary) / 0.35)",
                    backgroundColor: "rgb(var(--color-primary) / 0.10)",
                  }}
                >
                  {cart?.length || 0}
                </span>
              </div>

              <button
                type="button"
                onClick={() => dispatch(toggleCart())}
                className="h-9 w-9 rounded-full border border-gray-200 bg-white/80 flex items-center justify-center"
                aria-label="Close cart"
              >
                <CloseIcon className="!text-[20px] !text-primary" />
              </button>
            </div>
          </div>

          {/* Order Bar */}
          <div className="shrink-0 bg-white border-b border-gray-200">
            <div className="px-4 py-3">
              <div className="rounded-2xl border border-gray-200 bg-white shadow-sm px-3 py-2">
                {/* === ADDED BY SAMPATH – 2026-01-15 === */}
                {/* Keep switch row + description separate (not merged) */}
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <Pill className="bg-white">Order Type :</Pill>
                    <div className="h-5 w-px bg-gray-200" />

                    <div
                      className="flex items-center rounded-full border border-gray-200 overflow-hidden"
                      style={{
                        backgroundColor: "rgb(var(--color-primary) / 0.06)",
                        borderColor: "rgb(var(--color-primary) / 0.18)",
                      }}
                    >
                      <button
                        type="button"
                        disabled={isPickupDisabled}
                        onClick={() => changeDeliveryType("pickup")}
                        className={
                          "px-3 py-1.5 text-[12px] font-extrabold transition " +
                          (deliveryType === "pickup"
                            ? "text-light-text"
                            : "text-gray-700") +
                          (isPickupDisabled
                            ? " opacity-50 cursor-not-allowed"
                            : "")
                        }
                        style={
                          deliveryType === "pickup"
                            ? { backgroundColor: "rgb(var(--color-primary) / 1)" }
                            : {}
                        }
                      >
                        Pickup
                      </button>

                      <button
                        type="button"
                        disabled={isDeliveryDisabled}
                        onClick={() => changeDeliveryType("delivery")}
                        className={
                          "px-3 py-1.5 text-[12px] font-extrabold transition " +
                          (deliveryType !== "pickup"
                            ? "text-light-text"
                            : "text-gray-700") +
                          (isDeliveryDisabled
                            ? " opacity-50 cursor-not-allowed"
                            : "")
                        }
                        style={
                          deliveryType !== "pickup"
                            ? { backgroundColor: "rgb(var(--color-primary) / 1)" }
                            : {}
                        }
                      >
                        Delivery
                      </button>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => setDeleteDialog("ALL")}
                    className="px-4 py-2 rounded-full bg-primary !text-light-text font-extrabold text-[12px] shadow-sm active:scale-[0.99]"
                  >
                    Clear
                  </button>
                </div>

                {/* Description + note under order type */}
                <div className="mt-2 px-1">
                  <p
                    className="text-[12px] font-semibold"
                    style={{
                      color: "rgb(var(--color-primary) / 0.95)",
                    }}
                  >
                    You have{" "}
                    <span className="font-extrabold"></span> selected the (
                    {deliveryType === "pickup" ? "Pickup" : "Delivery"}) order
                    type.
                  </p>

                  <p
                    className="mt-1 text-[11px] leading-snug"
                    style={{
                      color: "rgb(var(--color-primary) / 0.70)",
                    }}
                  ></p>
                </div>
                {/* === END SAMPATH === */}
              </div>
            </div>
          </div>

          {/* Items list */}
          <div
            className="py-2 flex-1 overflow-y-auto"
            style={{
              paddingBottom: `calc(${footerHeight}px + env(safe-area-inset-bottom) + 12px)`,
              WebkitOverflowScrolling: "touch",
            }}
          >
            {cart.map((item, index) => (
              <Item
                item={item}
                index={index}
                onAction={handleCartAction}
                showDefaultToppings={settings?.DisplayDefaultToppings}
              />
            ))}
            {!cart.length && <p className="text-center">No Item Selected</p>}
          </div>

          {/* Footer */}
          <div
            ref={footerRef}
            className="shrink-0 sticky bottom-0 z-20 border-t border-gray-200"
            style={{
              backgroundColor: "rgb(255 255 255 / 0.96)",
              backdropFilter: "blur(10px)",
              paddingBottom: "calc(1rem + env(safe-area-inset-bottom))",
            }}
          >
            <div className="px-4 pt-4">
              <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                <div className="px-4 py-3">
                  {finalBill.map((i) => (
                    <>
                      {i.amt > 0 && (
                        <div className="flex items-center py-1.5">
                          <p className="font-bold text-[14px] !text-gray-600">
                            {i.title}
                          </p>
                          <DividerDot />
                          <p className="font-bold text-[14px] !text-gray-600">
                            ${i.amt.toFixed(2)}
                          </p>
                        </div>
                      )}
                    </>
                  ))}

                  {!!deliveryCharge && (
                    <div className="flex items-center py-1.5">
                      <p className="font-bold text-[14px] !text-gray-600">
                        Delivery Charge
                      </p>
                      <DividerDot />
                      <p className="font-bold text-[14px] !text-gray-600">
                        ${deliveryCharge.toFixed(2)}
                      </p>
                    </div>
                  )}

                  <div className="mt-2 pt-2 border-t border-gray-200 flex items-center">
                    <p className="font-extrabold text-[15px] !text-gray-900">
                      Total
                    </p>
                    <DividerDot />
                    <p className="font-extrabold text-[15px] !text-gray-900">
                      ${(total + deliveryCharge).toFixed(2)}
                    </p>
                  </div>
                </div>

                <div className="px-4 pb-4">
                  <Button
                    disabled={!cart.length}
                    className="!w-full !rounded-full !py-3.5 !font-[600] !bg-primary !text-light-text"
                    onClick={placeOrder}
                  >
                    Place Order
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Remove dialog */}
      <Dialog
        onClose={() => setDeleteDialog(undefined)}
        open={!!deleteDialog}
        hideBackdrop
        sx={{
          zIndex: REMOVE_DIALOG_ZINDEX,
          "& .MuiDialog-container": {
            alignItems: "flex-start",
            justifyContent: "flex-end",
            paddingTop: "calc(env(safe-area-inset-top) + 16px)",
            paddingRight: "16px",
            paddingLeft: "16px",
          },
        }}
        PaperProps={{
          sx: {
            width: "100%",
            maxWidth: 380,
            borderRadius: "14px",
            border: "1px solid rgb(var(--color-primary) / 0.18)",
            boxShadow: "0 18px 60px rgb(0 0 0 / 0.20)",
            backgroundColor: "rgb(255 255 255 / 0.96)",
            backdropFilter: "blur(10px)",
          },
        }}
      >
        <DialogTitle className="!font-bold">Remove from cart</DialogTitle>
        <DialogContent>
          <p>
            are you sure you want to remove{" "}
            {deleteDialog === "ALL"
              ? "All Items"
              : deleteDialog?.item.productName}
            {` `}
            from cart ?
          </p>
        </DialogContent>
        <DialogActions>
          <Button
            className="bg-white !text-primary"
            onClick={() => setDeleteDialog(undefined)}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (deleteDialog === "ALL") {
                dispatch(removeAllFromCart());
              } else {
                dispatch(removeFromCart(deleteDialog.index));
              }
              setDeleteDialog(undefined);
            }}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CartView;
